import { mapAsync } from '@/utils/array';
import { joinPath } from './file';

// Coverts linear color to gamma based on https://learnopengl.com/Advanced-Lighting/Gamma-Correction
function applyGammaCorrection(color) {
  return color ** (1 / 2.2);
}

function convertBaseColorToGammaRGB(colorArr) {
  return colorArr.map((c) => Math.round(applyGammaCorrection(c) * 255));
}

const TEXTURE_QUALITIES = ['initial', 'final_desktop', 'final_mobile'];

export function removeTexturesFromModel(model) {
  return {
    ...model,
    materials: (model.materials || []).map((material) => {
      return {
        ...material,
        pbrMetallicRoughness: {
          ...material.pbrMetallicRoughness,
          baseColorTexture: undefined,
          metallicRoughnessTexture: undefined,
        },
        normalTexture: undefined,
        occlusionTexture: undefined,
        emissiveTexture: undefined,
      };
    }),
    samplers: [],
    images: [],
    textures: [],
  };
}

export function parseTextureFile({ uri }) {
  return {
    name: uri.split('/').pop(),
    files: TEXTURE_QUALITIES.map((quality) => ({ name: quality })),
  };
}

export function parseTexture(
  { name, textureFile },
  { sampler },
  {
    model: { samplers },
    extensions: {
      KHR_texture_transform: {
        texCoord: transformTexCoord,
        offset: [uOffset, vOffset] = [],
        rotation: wAng,
        scale: [uScale, vScale] = [],
      } = {},
    } = {},
    texCoord = 0,
  },
) {
  const data = {
    textureFile,
    coordinatesIndex: transformTexCoord ?? texCoord,
    coordinatesMode: 'EXPLICIT',
    level: 1,
    samplingMode: 'LINEAR',
    uOffset,
    vOffset,
    uAng: 0,
    vAng: 0,
    wAng,
    uScale,
    vScale,
    hasAlpha: false,
  };

  if (Number.isInteger(sampler)) {
    ({ wrapS: data.wrapU, wrapT: data.wrapV } = samplers[sampler]);
  } else {
    data.wrapU = false;
    data.wrapV = false;
  }

  return {
    name,
    data,
  };
}

export async function parseMaterial(
  {
    name,
    pbrMetallicRoughness: {
      baseColorFactor,
      baseColorTexture,
      metallicFactor: metallic = 1,
      roughnessFactor: roughness = 1,
      metallicRoughnessTexture,
    },
    normalTexture,
    occlusionTexture,
    emissiveTexture,
    emissiveFactor = undefined,
    alphaMode = 'OPAQUE',
  },
  { processChannel },
) {
  const channels = [
    { channel: 'bump', channelsTexture: normalTexture },
    { channel: 'ambient', channelsTexture: occlusionTexture },
    { channel: 'emissive', channelsTexture: emissiveTexture },
    { channel: 'albedo', channelsTexture: baseColorTexture },
    { channel: 'metallic', channelsTexture: metallicRoughnessTexture },
  ].filter(({ channelsTexture }) => !!channelsTexture);

  const textures = await mapAsync(channels, processChannel);

  return {
    name,
    data: {
      type: 'PBR',
      alpha: baseColorFactor?.length === 4 ? baseColorFactor[3] : undefined,
      bakeAO: false,
      metallic,
      roughness,
      albedoColor: baseColorFactor && convertBaseColorToGammaRGB(baseColorFactor.slice(0, 3)),
      useDetailMap: false,
      bakeRoughness: true,
      emissiveColor: emissiveFactor && convertBaseColorToGammaRGB(emissiveFactor),
      invertNormalX: true,
      invertNormalY: false,
      bakeMetallness: true,
      transparencyMode: { OPAQUE: 'OPAQUE', MASK: 'ALPHATEST', BLEND: 'ALPHABLEND' }[alphaMode],
      useAlphaFromAlbedo: alphaMode === 'BLEND' && !!baseColorTexture,
      textures,
    },
  };
}

export function filterFilesIncludedInGLTF(files, { buffers = [], images = [], modelFile }) {
  const basePath = modelFile.fullPath.replace(modelFile.name, '');

  return files.filter(
    (file) =>
      modelFile.fullPath === file.fullPath ||
      buffers.find((binFile) => joinPath(basePath, binFile.uri) === file.fullPath) ||
      images.find((imageFile) =>
        TEXTURE_QUALITIES.some(
          (quality) =>
            joinPath(basePath, imageFile.uri.replace(/(initial|final_desktop|final_mobile)/, quality)) ===
            file.fullPath,
        ),
      ),
  );
}
